import { createEnum } from '@luna/utils-ts';

export enum AccountType {
    CURRENT = 'CURRENT',
    SAVING = 'SAVING',
    LOAN = 'LOAN',
    CARD_ACCOUNT = 'CARD_ACCOUNT',
    CREDIT_CARD = 'CREDIT_CARD',
    CRYPTO = 'CRYPTO',
    MORTGAGE = 'MORTGAGE',
    MONEY_MARKET_DEPOSIT = 'MONEY_MARKET_DEPOSIT',
}

export enum MultibankingAccountType {
    'EXT-BANK-CURRENT' = 'EXT-BANK-CURRENT',
}

export enum CardType {
    BANK_CARD = 'BANK_CARD',
    CREDIT = 'CREDIT',
    INDOOR = 'INDOOR',
    PREPAID = 'PREPAID',
    VIRTUAL_CREDIT = 'VIRTUAL_CREDIT',
    VIRTUAL_DEBIT = 'VIRTUAL_DEBIT',
}

export enum CurrentAccountSubtype {
    CURRENT_ACCOUNT = 'CURRENT_ACCOUNT',
    FOREIGN_ACCOUNT = 'FOREIGN_ACCOUNT',
    GIRO_ACCOUNT = 'GIRO_ACCOUNT',
}

export enum SavingsAccountSubtype {
    SAVING_ACCOUNT = 'SAVING_ACCOUNT',
    SAVING_ACCOUNT_FOR_CHILD_PASSBOOK = 'SAVING_ACCOUNT_FOR_CHILD_PASSBOOK',
    SAVING_ACCOUNT_FOR_HOUSING = 'SAVING_ACCOUNT_FOR_HOUSING',
    SAVING_ACCOUNT_FOR_KAPITAL_PASSBOOK = 'SAVING_ACCOUNT_FOR_KAPITAL_PASSBOOK',
    SAVING_ACCOUNT_FOR_PLEASURE = 'SAVING_ACCOUNT_FOR_PLEASURE',
    SAVING_ACCOUNT_FOR_RESERVE = 'SAVING_ACCOUNT_FOR_RESERVE',
    SAVING_ACCOUNT_FOR_SPOROBONUS_PASSBOOK = 'SAVING_ACCOUNT_FOR_SPOROBONUS_PASSBOOK',
    SAVING_ACCOUNT_PIGGY = 'SAVING_ACCOUNT_PIGGY',
    SAVING_CS = 'SAVING_CS',
    SAVING_PLUS = 'SAVING_PLUS',
    TERM_DEPOSIT = 'TERM_DEPOSIT',
    TERM_DEPOSIT_PASSBOOK = 'TERM_DEPOSIT_PASSBOOK',
    TERM_DEPOSIT_SAVINGS_PLAN = 'TERM_DEPOSIT_SAVINGS_PLAN',
}

enum LoanAccountSubtype {
    LOAN_ACCOUNT = 'LOAN_ACCOUNT',
    MORTGAGE = 'MORTGAGE',
    LEASING = 'LEASING',
    REVOLVING_LOAN = 'REVOLVING_LOAN',
}

export enum CreditCardAccountSubtype {
    CREDIT_CARD_ACCOUNT = 'CREDIT_CARD_ACCOUNT',
}

export enum InvestmentAccountSubtype {
    AT_SECURITIES_CORPORATE_SETTLEMENT = 'AT_SECURITIES_CORPORATE_SETTLEMENT',
    AT_SECURITIES_DIGIVV_SETTLEMENT = 'AT_SECURITIES_DIGIVV_SETTLEMENT',
    AT_SECURITIES_FOND_SETTLEMENT = 'AT_SECURITIES_FOND_SETTLEMENT',
    AT_SECURITIES_RETAIL_SETTLEMENT = 'AT_SECURITIES_RETAIL_SETTLEMENT',
    INVESTMENT_CURRENT_ACCOUNT = 'INVESTMENT_CURRENT_ACCOUNT',
    INVESTMENT_FOREIGN_ACCOUNT = 'INVESTMENT_FOREIGN_ACCOUNT',
}

const InsuranceAccountSubtype = createEnum('LIFE', 'PROPERTY');
type InsuranceAccountSubtype = createEnum<typeof InsuranceAccountSubtype>;

export type AccountSubtype =
    | CurrentAccountSubtype
    | SavingsAccountSubtype
    | LoanAccountSubtype
    | CreditCardAccountSubtype
    | InvestmentAccountSubtype
    | InsuranceAccountSubtype
    | ProductSubType;

export enum ProductSubType {
    BUILDINGSOCIETYSAVING = 'BUILDINGSOCIETYSAVING',
    BUILDINGSOCIETYLOAN = 'BUILDINGSOCIETYLOAN',
    MORTGAGE = 'MORTGAGE',
}

export const AccountSubtype = {
    ...CurrentAccountSubtype,
    ...SavingsAccountSubtype,
    ...LoanAccountSubtype,
    ...CreditCardAccountSubtype,
    ...InvestmentAccountSubtype,
    ...InsuranceAccountSubtype,
    ...ProductSubType,
};

export enum Periodicity {
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY',
    HALFYEARLY = 'HALFYEARLY',
    YEARLY = 'YEARLY',
}

export enum InterestBooking {
    CASH = 'CASH',
    TERM_DEPOSIT = 'TERM_DEPOSIT',
    LINKED_ACCOUNT = 'LINKED_ACCOUNT',
}

export enum InstallmentFrequency {
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY',
    HALFYEARLY = 'HALFYEARLY',
    YEARLY = 'YEARLY',
    '3_YEARLY' = '3_YEARLY',
    '5_YEARLY' = '5_YEARLY',
    IRREGULAR = 'IRREGULAR',
}

export enum LimitPeriod {
    '1D' = '1D',
    '7D' = '7D',
    '14D' = '14D',
    '30D' = '30D',
}

export const FLAG = createEnum(
    'accountQueryAllowed',
    // transactionQueryAllowed is a temporary flag once CZ BE removes it, should be removed from here as well
    'transactionQueryAllowed',
    'ownTransferAllowed',
    'domesticTransferAllowed',
    'internationalTransferAllowed',
    'taxTransferAllowed',
    'loanRepaymentAllowed',
    'urgentTransferAllowed',
    'electronicStatementAllowed',
    'offline',
    'owner',
    'collectiveSigning',
    'dcsAllowed',
    'cancelled',
    'deletable',
    'individualConditionsAllowed',
    'directDebitMandateManagementAllowed',
    'directDebitAllowed',
    'sipoDirectDebitAllowed',
    'directDebitCollectionAllowed',
    'sepaDirectDebitCollectionAllowed',
    'directDebitReadOnly',
    'standingOrderNotAllowed',
    'standingOrderAllowed',
    'standingOrderReadOnly',
    'accountConditionChangeAllowed',
    'cancellationAllowed',
    'cancellationAtMaturityAllowed',
    'overdraftLimitChangeAllowed',
    'overdraftLimitSetupAllowed',
    'savingEnvelopesActiveOperationsNotAllowed',
    'loanDrawdownAllowed',
    'ownSavingAllowed',
    'transferDateTodayOnly',
    'transferDateNextProlongationOnly',
    'dataCarrierUpload',
    'dataCarrierDownload',
    'roundingsavingsActivated',
    'komfortKreditAccount',
    'closed',
    'statementQueryAllowed',
    'depositBound',
    'signedStatementAllowed',
    'cz-revolvingLoanReimbursementAllowed',
    'cz-revolvingLoanDisbursementAllowed',
    'subaccountOpeningAllowed',
    'purchaseDebitCardAllowed',
    'loanRepaymentAllowed',
    'loanRepaymentNotAvailableDueToBankProcessing',
    'highRiskCreditRating',
    'cz-savingEnvelopesCreationAllowed',
    'moreMulticurrencyAvailable',
    'requestToPayAllowed',
    'targetAmountChangeAllowed',
    'cz-mobilePaymentsAllowed',
    'cardInstallmentsEntryAllowed',
    'ro-billPaymentAllowed',
    'virtualCard',
    'sk-notificationAllowed',
    'beneficiariesAllowed',
    'contributionsAllowed',
    'bulkCreditTransferAllowed',
    'at_mbsBalanceNotAvailable',
    'viewBalanceAllowed',
    'confidentialTransferAllowed',
    'deletableMultiBankingAccount',
    'ecoAccount',
    'ro-directDebitRedirectAllowed',
    'cardInstallmentsAllowed',
    'performanceDataAllowed',
    'purchasePermanentVirtualCardAllowed',
    'purchaseLimitedVirtualCardAllowed',
    'aliasLicenseChangeAllowed',
    'hr-legacyCurrencyAccount',
    'onlinePensionContributionAllowed',
    'startSigningAllowed',
    'savingEnvelopesConsentRequired',
    'savingEnvelopesCreationAllowed',
    'syncInProgress',
    'cz-hasProductFeaturePPI',
    'accountNumberNotAvailable',
    'loanAccount',
    'hu-freeCashWithdrawalAllowed',
    'roundUpSourceAllowed',
    'junior',
    'closedPortfolio',
    'cardRepayment',
    'hr-keksAccount',
    'insuredProduct',
    'outstandingsEnabled',
    'subproductGroupsEnabled',
    'assetManagementAcc',
    'consignmentContractAcc',
    'searchAndBuyNotAllowed',
    'securitiesOrdersNotAllowed',
    'securitiesStatementsNotAllowed',
    'recurrentOrdersNotAllowed',
    'pieChartNotAllowed',
    'titlesListNotAllowed',
    'performanceGraphNotAllowed',
    'goldAccount',
    'trueWealthAccount',
    'notAllowedToTrade',
    'owner',
    'capitalGainTax',
    'brokerjet',
    'MISC_PRODUCT',
    'syncError',
    'showPricingModelAllowed',
    'savingsAccount',
    'notAllFundsInBalanceInvested',
    'instantPaymentLimit',
    'instantPaymentLimitChangeAllowed',
    'moneyAutomationSourceAllowed',
    'nonResident',
    'authorizedAccount',
    'secondaryIdAllowed',
    'contractualStandingOrderEnabled',
    'topUpAllowed',
    'debitCardOrderingAllowed',
    'selfServiceAllowed',
);
export type FLAG = createEnum<typeof FLAG>;

export const USABLE_FOR = createEnum(
    'savingsOpening',
    'loanProcessing',
    'datacarrier',
    'dataCarrierUpload',
    'dataCarrierDownload',
    'roundingSavings',
    'roundupReceiver',
    'pluginSettlement',
    'cardTanReader',
    'sPrepaidCards',
    'sCreditCards',
    'buildingSavingsOpening',
    'buildingSavingsClosing',
    'accidentInsurance',
    'quickTopupSFondsplan',
);
export type USABLE_FOR = createEnum<typeof USABLE_FOR>;

export enum StatementCreationPeriodicity {
    DAILY = 'DAILY',
    WEEKLY = 'WEEKLY',
    MIDMONTHULTIMO = 'MIDMONTHULTIMO',
    MONTHLY = 'MONTHLY',
    QUARTERLY = 'QUARTERLY',
    HALFYEARLY = 'HALFYEARLY',
    YEARLY = 'YEARLY',
    SBELECTRONIC = 'SBELECTRONIC',
    SCREDITCARDSTATEMENT = 'SCREDITCARDSTATEMENT',
    UNDEFINED = 'UNDEFINED',
}

export const DIRECT_DEBIT_SCHEME = createEnum('CORE', 'B2B', 'ALL');

export type DIRECT_DEBIT_SCHEME = createEnum<typeof DIRECT_DEBIT_SCHEME>;
